/**
 * System Stats API (client-safe)
 *
 * Server functions for system stats. No .server suffix so client code
 * (e.g. useSystemStats, dashboard, stats route) can import. Handlers run only on the server.
 *
 * Pattern: server function (here) → hook (useSystemStats) → UI route.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

/**
 * Get system statistics (mock data for demo purposes).
 * In a real app, this would query the database or external services.
 *
 * Authorization: `accessToken` is optional. When provided, the caller is
 * verified via Supabase Auth before returning data. When omitted (e.g. from
 * server-side route loaders or the useSystemStats hook), the request is
 * treated as a trusted server-side call and returns mock data directly.
 */
export const getSystemStats = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema.optional(),
    })
  )
  .handler(async ({ data }) => {
    // When an accessToken is provided, verify the caller
    if (data.accessToken) {
      const { createAdminClient } = await import("./_admin-utils.server");
      const { client: adminClient, error: initError } = await createAdminClient();
      if (initError || !adminClient) {
        return { data: null, error: initError ?? "Failed to init client" };
      }

      const {
        data: { user: caller },
        error: authError,
      } = await adminClient.auth.getUser(data.accessToken);
      if (authError || !caller) {
        return { data: null, error: "Unauthorized" };
      }
    }

    // Simulate a database query or API call
    await new Promise((resolve) => setTimeout(resolve, 100));

    // In production, this would query Supabase or other data sources
    const stats = {
      totalUsers: 142,
      totalTodos: 389,
      totalNotifications: 24,
      activeProjects: 28,
      completedTasks: 1247,
      uptime: "99.9%",
      lastUpdated: new Date().toISOString(),
    };

    return { data: stats, error: null };
  });

/**
 * Get user activity summary (mock data for demo)
 */
export const getUserActivitySummary = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: z.string(),
    })
  )
  .handler(async ({ data }) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 150));

    // Mock activity data
    const activity = {
      userId: data.userId,
      todayTasks: 5,
      thisWeekTasks: 23,
      thisMonthTasks: 87,
      streak: 12,
      lastActive: new Date().toISOString(),
    };

    return { data: activity, error: null };
  });
